import { isArray, isEmpty, isNull, isString } from 'lodash';
import { Fragment, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Badge, Banner, Card, SanitizedHtml, Skeleton, ZendeskLink } from 'components';
import { formatLocalizedCurrency, getBadgeColor } from 'helpers';
import { useFetch, useSafeState, useUser } from 'hooks';
import { routes } from 'router';

const LeisureOrderShow = () => {
  const { call: orderCall, loading: orderLoading, data: leisureOrder } = useFetch();
  const { call: buyboxTicketsCall, loading: buyboxTicketsLoading } = useFetch();
  const { currentUser } = useUser();
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [eticketLinks, setEticketLinks] = useSafeState([]);
  const [eTicketComment, setEticketComment] = useSafeState('');

  const summary = useMemo(
    () => [
      { i18nKey: 'reference', value: leisureOrder?.ref },
      { i18nKey: 'offer', value: leisureOrder?.lines },
      { i18nKey: 'status', value: leisureOrder?.status },
      { i18nKey: 'createDate', value: leisureOrder?.date },
      { i18nKey: 'priceBefore', value: formatLocalizedCurrency(leisureOrder?.order?.amount) },
      { i18nKey: 'priceAfter', value: formatLocalizedCurrency(leisureOrder?.order?.amountAfterSubsidy) }
    ],
    [leisureOrder]
  );

  const buyboxLines = useMemo(() => leisureOrder?.lines?.filter((line) => !isNull(line?.buyboxInfo)), [leisureOrder]);

  useEffect(() => {
    getLeisureOrder();
  }, [currentUser]);

  useEffect(() => {
    if (leisureOrder) initEticketLinks();
  }, [leisureOrder]);

  const getLeisureOrder = () => {
    orderCall({
      service: 'castelis',
      path: `order/${currentUser.serialNumber}/leisureOrderDetail/${id}`,
      method: 'get'
    });
  };

  const getBuyboxTickets = (customRef) => {
    return buyboxTicketsCall({
      service: 'buybox',
      method: 'get',
      path: `/api/giftcards/${customRef}`
    });
  };

  const getAllBuyboxTickets = async () => {
    // For each buybox, get the ticket link from the customRef
    const buyboxTickets = buyboxLines?.flatMap((line) => {
      const refs = line?.buyboxInfo?.customRefs;
      if (isEmpty(refs)) return [];
      return refs.map((customRef, index) => ({
        buyboxName: line?.title ?? t('dashboard:eTickets.giftcardDefaultName', { number: index + 1 }),
        customRef
      }));
    });
    if (isEmpty(buyboxTickets)) return;
    const buyboxTicketsPromises = buyboxTickets.map((ticket) => getBuyboxTickets(ticket.customRef));
    const responses = await Promise.all(buyboxTicketsPromises);

    if (responses) {
      const allTickets = responses.map((response) => response?.data?.body) || [];
      const allTicketsLinks = allTickets
        .map((ticket, index) => {
          const url = ticket?.giftCardLink;
          if (!url) return null;
          return {
            url,
            buybox: true,
            name: buyboxTickets?.[index]?.buyboxName ?? t('buttons.download'),
            label: t('dashboard:show.eTickets.giftcardDefaultName', { number: index + 1 })
          };
        })
        .filter(Boolean); // Remove null values

      return allTicketsLinks;
    }
  };

  const goBack = () => {
    navigate(routes.dashboard.children.orders.path);
  };

  const initEticketLinks = async () => {
    let allTicketsLinks = [];
    const fileLinks = leisureOrder?.eticketFileLinks;

    if (!isEmpty(buyboxLines)) {
      const buyboxTickets = await getAllBuyboxTickets();
      allTicketsLinks = [...allTicketsLinks, ...(buyboxTickets || [])];
    }

    if (!isEmpty(fileLinks)) {
      // API returns a string if links are not created, and an array if there are links
      // TODO : Check if API can return an empty array instead of a string
      if (isString(fileLinks)) {
        setEticketComment(fileLinks);
      } else if (isArray(fileLinks)) {
        const formattedFileLinks = fileLinks?.map((url, index) => {
          const urlObject = new URL(url);
          const fileId = urlObject.searchParams.get('file_id');
          return {
            url: `${url}&token=${currentUser.token}`,
            buybox: false,
            name: fileId ?? t('buttons.download'),
            label: t('dashboard:show.eTickets.name', { number: index + 1 })
          };
        });

        allTicketsLinks = [...allTicketsLinks, ...(currentUser?.token ? formattedFileLinks : [])];
      }
    }

    setEticketLinks(allTicketsLinks);
  };

  return (
    <>
      {orderLoading && (
        <div className="grid grid-cols-12 lg:gap-8">
          <div className="col-span-12 lg:col-span-7">
            <Skeleton type="text" classNames="h-12 w-72 mt-8  lg:hidden" />
            <Skeleton type="text" classNames="h-72 w-full lg:h-[98.5%]" />
          </div>
          <div className="col-span-12 lg:col-span-5">
            <div>
              <Skeleton type="text" classNames="h-12 w-72 mt-8 lg:gap-8 lg:hidden" />
              <Skeleton type="text" classNames="h-48 w-full" />
            </div>
            <div>
              <Skeleton type="text" classNames="h-12 w-72 mt-8 lg:hidden" />
              <Skeleton type="text" classNames="h-64 w-full" />
            </div>
          </div>
        </div>
      )}

      {leisureOrder && !isString(leisureOrder) && !orderLoading && (
        <>
          <button type="button" className="btn uppercase mb-6 small" onClick={goBack}>
            {t('buttons.goBack')}
          </button>

          <div className="grid grid-cols-12 gap-y-8 lg:gap-8">
            <div className="col-span-12 lg:col-span-7 flex flex-col gap-8">
              <Card onMobile={false} className="relative pb-16">
                <div className="flex-center-between mb-8">
                  <h2 className="h2 underlined-yellow">{t('dashboard:show.summary')}</h2>

                  <div className="flex justify-end gap-4 absolute bottom-0 right-0 md:static">
                    {!isEmpty(leisureOrder.docs) && (
                      <a href={`${leisureOrder.docs}&token=${currentUser.token}`} className="btn small outlined">
                        {t('dashboard:show.invoice')}
                      </a>
                    )}

                    <ZendeskLink to={process.env.REACT_APP_ZENDESK_URL_ORDER_DETAIL} className="btn small">
                      {t('dashboard:show.manage')}
                    </ZendeskLink>
                  </div>
                </div>

                <div className="grid grid-cols-12 gap-6 text-gray-600">
                  {summary?.map((item, i) => (
                    <Fragment key={`summary-${i}`}>
                      <div className="col-span-3 uppercase font-bold">{t(`dashboard:show.${item.i18nKey}`)}</div>

                      <div className="col-span-9">
                        {isArray(item?.value) && (
                          <div className="flex flex-col gap-3">
                            {item.value?.map((value, j) => (
                              <li key={`summary-value-${j}`}>
                                {t('dashboard:show.offerQuantity', { quantity: value.quantity, title: value.title })}
                              </li>
                            ))}
                          </div>
                        )}

                        {item.i18nKey === 'status' && (
                          <Badge color={getBadgeColor(item.value)} className="text-sm">
                            {item.value}
                          </Badge>
                        )}

                        {!isArray(item.value) && item.i18nKey !== 'status' && item.value}
                      </div>
                    </Fragment>
                  ))}
                </div>
              </Card>
            </div>

            <div className="col-span-12 flex flex-col lg:gap-8 lg:col-span-5">
              {buyboxTicketsLoading && <Skeleton type="text" classNames="h-48 w-full" />}

              {!buyboxTicketsLoading && (
                <>
                  {(!isEmpty(eticketLinks) || !isEmpty(eTicketComment)) && (
                    <Card onMobile={false} className="relative pb-16">
                      <h2 className="h2 underlined-yellow mb-8">{t('dashboard:show.eTickets.title')}</h2>

                      <ul>
                        {eticketLinks?.map((link, index) => (
                          <li className="mb-2" key={`download-link-${index}`}>
                            {link.label}
                            <a
                              className="text-primary-500 underline underline-offset-2 hover:no-underline"
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {link.name}
                            </a>
                          </li>
                        ))}
                      </ul>

                      {!isEmpty(eTicketComment) && isEmpty(leisureOrder?.comments) && <p>{eTicketComment}</p>}
                    </Card>
                  )}
                </>
              )}

              {!isEmpty(leisureOrder?.comments) && (
                <Card onMobile={false}>
                  <h2 className="h2 underlined-yellow mb-8">{t('dashboard:show.eTickets.title')}</h2>
                  <SanitizedHtml content={leisureOrder.comments} className="break-text" />
                </Card>
              )}

              <Card onMobile={false}>
                <h2 className="h2 underlined-yellow mb-8">{t('dashboard:show.delivery')}</h2>

                <div className="grid grid-cols-12 gap-4 text-gray-600">
                  <p className="col-span-5 uppercase font-bold">{t('dashboard:show.type')}</p>
                  <p className="col-span-7">{leisureOrder.delivery.method}</p>

                  <p className="col-span-5 uppercase font-bold">{t('dashboard:show.address')}</p>
                  <div className="col-span-7 flex flex-col">
                    <p>
                      {leisureOrder.delivery.firstName} {leisureOrder.delivery.name}
                    </p>
                    <p>{leisureOrder.delivery.address1}</p>
                    <p>
                      {leisureOrder.delivery.postalCode} {leisureOrder.delivery.city}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </>
      )}

      {leisureOrder && isString(leisureOrder) && !orderLoading && (
        <Banner type="warning" icon={{ name: 'circle-info', style: 'fad' }}>
          <span>{t('contentUnavailable')}</span>
        </Banner>
      )}
    </>
  );
};

export default LeisureOrderShow;
